import EditIcon from '@mui/icons-material/Edit'
import { Box, Checkbox, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'

import { AlertDialog, MButton, SvgImage } from 'components'
import { ADMIN_CONTACT_EMAIL } from 'config'
import { color } from 'config/color'
import { useState } from 'react'
import { isAuthorized, useProvideAuth } from 'utils'
import { AssetDialog } from './AssetDialog'
import { EditAssetDialog } from './EditAssetDialog'

interface IAssetListCard {
  id: number
  filename: string
  url: string
  updatedAt: string
  selectedAssets: any
  setSelectedAssets: any
  setIsLoading: any
  handleAssets: () => Promise<void>
  setDialogData: any
}

export const AssetListCard = (props: IAssetListCard): JSX.Element => {
  const [isRenaming, setIsRenaming] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { deleteAssets, handleLogOut } = useProvideAuth()

  const [confirmDialog, setConfirmDialog] = useState<any>({
    type: 'warning',
    isOpen: false,
    title: 'Deleting Asset',
    message:
      'You’re about to remove an asset. Please be aware that this action can’t be undone.'
  })

  const handleSingleDelete = async (showConfirmDialog: boolean): Promise<void> => {
    try {
      if (showConfirmDialog) {
        setConfirmDialog({
          ...confirmDialog,
          isOpen: true
        })
        return
      }
      props.setIsLoading(true)
      await deleteAssets(props.filename)
      props.setDialogData({
        type: 'success',
        isOpen: true,
        title: 'Asset deleted',
        message:
          'You have successfully deleted an asset. You can add more assets under Assets'
      })
      props.setIsLoading(false)
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()
      props.setDialogData({
        type: 'error',
        isOpen: true,
        title: 'An error occured',
        message: `We are really sorry but there was a problem and we couldn’t delete the asset. Please try again, if the error persist, <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>. `
      })
    }
  }

  const isSelected = !isEmpty(
    props.selectedAssets?.find((item: { key: string }) => item.key === props.filename)
  )
  const handleAddSelected = (event: { target: { checked: boolean } }): void => {
    if (event.target.checked) {
      props.setSelectedAssets([
        ...props.selectedAssets,
        { id: props.id, key: props.filename, lastModifiedDate: props.updatedAt }
      ])
    } else {
      props.setSelectedAssets(
        props.selectedAssets.filter(
          (item: { key: string }) => item.key !== props.filename
        )
      )
    }
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        border={`1px solid ${isSelected ? color.primary[600] : color.secondary.dark[100]
          }`}
        height="20vh"
        justifyContent="space-between"
      >
        <Box
          height="10%"
          display="flex"
          bgcolor={color.grey[50]}
          justifyContent="flex-end"
          alignItems="flex-start"
        >
          <Checkbox
            name="selected"
            icon={<SvgImage name="CheckboxOutline" width={20} height={20} />}
            checkedIcon={<SvgImage name="CheckboxIconBlue" width={20} height={20} />}
            checked={isSelected}
            onChange={handleAddSelected}
          />
        </Box>

        <Box
          height="50%"
          display="flex"
          bgcolor={color.grey[50]}
          justifyContent="center"
          onClick={() => { setIsOpen(!isOpen) }}
          sx={{ cursor: 'pointer' }}
        >
          <Box component="img" src={props.url} alt="background" />
        </Box>

        <Box
          height="40%"
          display="flex"
          bgcolor={color.secondary.dark[100]}
          p={0.75}
          flexDirection="column"
          justifyContent="center"
        >
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                color={color.grey[800]}
                fontWeight={400}
                fontSize={16}
              >
                {props.filename}
              </Typography>
              <IconButton
                onClick={() => {
                  setIsRenaming(!isRenaming)
                }}
              >
                <EditIcon
                  fontSize="small"
                  sx={{ color: color.secondary.dark[500] }}
                />
              </IconButton>
            </Box>

            <MButton
              component="label"
              type="button"
              size="medium"
              rounded="true"
              sx={{ padding: 0 }}
              texttransform="none"
              onClick={async () => {
                await handleSingleDelete(true)
              }}
            >
              <Typography fontSize={16} color={color.error[600]}>
                Delete
              </Typography>
            </MButton>
          </Box>

          <Typography color={color.grey[600]} fontWeight={400} fontSize={12}>
            {`Last Updated ${dayjs(props.updatedAt).format('DD MMM YYYY')}`}
          </Typography>
        </Box>
      </Box>
      <AssetDialog isOpen={isOpen} handleClose={() => { setIsOpen(!isOpen) }} fileName={props.filename} url={props.url} />
      <EditAssetDialog
        setDialogData={props.setDialogData}
        isOpen={isRenaming}
        handleClose={() => {
          setIsRenaming(!isRenaming)
        }}
        fileName={props.filename}
        url={props.url}
      />
      <AlertDialog
        open={confirmDialog.isOpen}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        handleClick={() => {
          setConfirmDialog({ ...confirmDialog, isOpen: false })
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap={1}
        >
          <MButton
            variant="contained"
            type="button"
            rounded="true"
            texttransform="none"
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, isOpen: false })
              void handleSingleDelete(false)
            }}
          >
            Delete Asset
          </MButton>
          <MButton
            variant="outlined"
            type="button"
            rounded="true"
            texttransform="none"
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, isOpen: false })
            }}
          >
            Go Back to Assets List
          </MButton>
        </Box>
      </AlertDialog>
    </>
  )
}
